/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import type { FlipperSpec, TableInfoSpec } from "../../../shared/contracts";
import { ItemData, toVertex2D } from "../../core/item-data";
import type { Vertex2D } from "../../math/vertex2d";

/**
 * The angle convention of a table spec is the one of the table data and the renderer. The angle
 * is in degrees from the +x axis. It grows clockwise on the screen (toward +y, down the table).
 * A flipper at 30 degrees points to the right and down. The upstream code measures from the up
 * axis, so 0 points up the table. The engine adds this offset to a spec angle.
 */
export const SPEC_TO_UPSTREAM_ANGLE_DEG = 90;

/** Upstream default values of the flipper physics. A spec value replaces them. */
const DEFAULT_MASS = 1;
const DEFAULT_STRENGTH = 2200;
const DEFAULT_ELASTICITY = 0.8;
const DEFAULT_ELASTICITY_FALLOFF = 0.43;
const DEFAULT_FRICTION = 0.6;
const DEFAULT_RETURN = 0.058;
const DEFAULT_RAMP_UP = 3.0;
const DEFAULT_TORQUE_DAMPING = 0.75;
const DEFAULT_TORQUE_DAMPING_ANGLE = 6.0;
const DEFAULT_SCATTER = 0;

export class FlipperData extends ItemData {
  public baseRadius: number = 21.5;
  public endRadius: number = 13.0;
  public flipperRadiusMin: number = 0;
  public flipperRadiusMax: number = 130.0;
  public flipperRadius: number = 130.0;

  public startAngle: number = 121.0;
  public endAngle: number = 70.0;
  public height: number = 50.0;

  public center: Vertex2D;

  public szSurface?: string;
  public szMaterial?: string;

  public szRubberMaterial?: string;
  public rubberThickness: number = 7.0;
  public rubberHeight: number = 19.0;
  public rubberWidth: number = 24.0;

  public mass: number = DEFAULT_MASS;
  public strength: number = DEFAULT_STRENGTH;
  public elasticity: number = DEFAULT_ELASTICITY;
  public elasticityFalloff: number = DEFAULT_ELASTICITY_FALLOFF;
  public friction: number = DEFAULT_FRICTION;
  public return: number = DEFAULT_RETURN;
  public rampUp: number = DEFAULT_RAMP_UP;
  public torqueDamping: number = DEFAULT_TORQUE_DAMPING;
  public torqueDampingAngle: number = DEFAULT_TORQUE_DAMPING_ANGLE;
  public scatter: number = DEFAULT_SCATTER;

  public isVisible: boolean = true;
  public isEnabled: boolean = true;

  /** Which button moves the flipper. */
  public side: "left" | "right" = "left";

  public constructor(itemName: string, center: Vertex2D) {
    super(itemName);
    this.center = center;
  }

  /** Builds the data from a table spec. A field that the spec leaves out keeps its upstream default. */
  public static fromSpec(spec: FlipperSpec, _info?: TableInfoSpec): FlipperData {
    const data = new FlipperData(spec.name, toVertex2D(spec.center));
    data.baseRadius = spec.baseRadius;
    data.endRadius = spec.endRadius;
    data.flipperRadiusMax = spec.flipperRadiusMax;
    data.flipperRadius = spec.flipperRadiusMax;
    data.startAngle = spec.startAngle + SPEC_TO_UPSTREAM_ANGLE_DEG;
    data.endAngle = spec.endAngle + SPEC_TO_UPSTREAM_ANGLE_DEG;
    data.height = spec.height;
    data.szSurface = spec.szSurface;
    data.szMaterial = spec.szMaterial;
    data.szRubberMaterial = spec.szRubberMaterial;
    data.rubberThickness = spec.rubberThickness ?? data.rubberThickness;
    data.rubberHeight = spec.rubberHeight ?? data.rubberHeight;
    data.rubberWidth = spec.rubberWidth ?? data.rubberWidth;
    data.mass = spec.mass ?? data.mass;
    data.strength = spec.strength ?? data.strength;
    data.elasticity = spec.elasticity ?? data.elasticity;
    data.elasticityFalloff = spec.elasticityFalloff ?? data.elasticityFalloff;
    data.friction = spec.friction ?? data.friction;
    data.return = spec.return ?? data.return;
    data.rampUp = spec.rampUp ?? data.rampUp;
    data.torqueDamping = spec.torqueDamping ?? data.torqueDamping;
    data.torqueDampingAngle = spec.torqueDampingAngle ?? data.torqueDampingAngle;
    data.scatter = spec.scatter ?? data.scatter;
    data.isVisible = spec.isVisible ?? true;
    data.isEnabled = spec.isEnabled ?? true;
    data.side = spec.side;
    return data;
  }
}
